/**
 * CodeNode Types - Secure code execution node
 *
 * Supports Python and JavaScript execution in isolated Cloudflare Sandbox containers.
 * Results render inline with support for rich outputs (charts, tables, images).
 *
 * Design principles:
 * - Jupyter notebook-style persistent sessions (variables survive between runs)
 * - Rich output rendering (matplotlib charts, pandas tables, images)
 * - Real-time streaming of stdout/stderr
 * - Editable code with syntax highlighting
 */

// Import shared execution types to avoid duplication
import type { ExecutionResult, RichOutput, ExecutionError } from './execution';

// Re-export for convenience (consumers can import from code-node or execution)
export type { ExecutionResult, RichOutput, ExecutionError };

// ============================================================================
// LANGUAGE TYPES
// ============================================================================

/** Supported programming languages for code execution */
export type CodeLanguage = 'python' | 'javascript';

/** Display information for languages */
export interface LanguageInfo {
  id: CodeLanguage;
  name: string;
  icon: string;  // Emoji or icon identifier
  fileExtension: string;
  defaultCode: string;
}

/** Default language configurations */
export const CODE_LANGUAGES: LanguageInfo[] = [
  {
    id: 'python',
    name: 'Python',
    icon: '🐍',
    fileExtension: '.py',
    defaultCode: '# Python code\nprint("Hello, World!")\n',
  },
  {
    id: 'javascript',
    name: 'JavaScript',
    icon: '📜',
    fileExtension: '.js',
    defaultCode: '// JavaScript code\nconsole.log("Hello, World!");\n',
  },
];

// ============================================================================
// EXECUTION STATUS
// ============================================================================

/** Execution status for the code node */
export type ExecutionStatus = 'idle' | 'running' | 'complete' | 'error';

// ============================================================================
// NODE DATA
// ============================================================================

/**
 * Data stored in a CodeNode
 *
 * This is the primary interface for CodeNode state. It includes:
 * - Content (code, language, title)
 * - Execution state (status, current result, history)
 * - Settings (auto-run behavior)
 * - Runtime callbacks (injected by BoardCanvas)
 */
export interface CodeNodeData {
  // ========== Content ==========

  /** Node title (displayed above the node) */
  title: string;

  /** Code content in the editor */
  code: string;

  /** Selected programming language */
  language: CodeLanguage;

  // ========== Execution State ==========

  /** Current execution status */
  status: ExecutionStatus;

  /** Result from the most recent execution */
  currentResult?: ExecutionResult;

  /** History of previous executions (for scrollback) */
  executionHistory: ExecutionResult[];

  // ========== Settings ==========

  /** Auto-run when AI writes code to this node */
  autoRun: boolean;

  // ========== Callbacks (injected at runtime by BoardCanvas) ==========

  /** Called when title changes */
  onTitleChange?: (title: string) => void;

  /** Called when code changes */
  onCodeChange?: (code: string) => void;

  /** Called when language changes */
  onLanguageChange?: (language: CodeLanguage) => void;

  /** Called when user triggers execution */
  onExecute?: () => void;

  // ========== Runtime Context (injected by BoardCanvas) ==========

  /** Yjs document for collaboration (not stored in Yjs) */
  ydoc?: unknown;

  /** Board ID for API calls (not stored in Yjs) */
  boardId?: string;

  /** Yjs map of all nodes (not stored in Yjs) */
  nodesMap?: unknown;

  /** Yjs map of all edges (not stored in Yjs) */
  edgesMap?: unknown;

  /** Change wrapper for undo/redo support (not stored in Yjs) */
  makeChange?: (fn: () => void) => void;

  // Index signature for React Flow compatibility
  [key: string]: unknown;
}

// ============================================================================
// REACT FLOW NODE TYPE
// ============================================================================

/**
 * React Flow node type for CodeNode
 *
 * Use this type when working with code nodes in React Flow:
 * - In nodeTypes registry
 * - When creating new code nodes
 * - In onNodesChange handlers
 */
export type CodeNodeType = {
  id: string;
  type: 'code';
  data: CodeNodeData;
  position: { x: number; y: number };
  [key: string]: unknown;
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Create default CodeNodeData with sensible defaults
 *
 * Use this when creating a new CodeNode to ensure all required
 * fields are initialized with appropriate default values.
 */
export function createDefaultCodeNodeData(): CodeNodeData {
  return {
    title: '',
    code: CODE_LANGUAGES[0].defaultCode,  // Python default
    language: 'python',
    status: 'idle',
    executionHistory: [],
    autoRun: true,  // AI-written code auto-runs by default
  };
}

/**
 * Get language info by ID
 *
 * Useful for displaying language icons, names, etc.
 */
export function getLanguageInfo(language: CodeLanguage): LanguageInfo {
  return CODE_LANGUAGES.find(l => l.id === language) || CODE_LANGUAGES[0];
}
